/**
 * Chat principal da sala (system/mainChat; FCRep.BaseChatLayout).
 *
 * Formato do log como na mesa de referência:
 *   [20:48] ▮ Nome saiu
 *   🎲 Nome rolou 1d8 + 1 = 6 {[5] + 1}
 *   ——— 10 de outubro ———        (separador de data)
 * Linhas seguidas do mesmo autor omitem o horário. A mensagem de boas-vindas
 * da sala aparece no fim, entre linhas.
 *
 * Entrada: caixa com o texto em cima e as ferramentas embaixo (negrito,
 * itálico, sublinhado, cor, falar como; e uma fileira de dados d4…d100).
 * Comandos: gerente de comandos em ../chatCommands.ts (docs/spec/chat-commands.md).
 * Linhas de sistema (entrou, saiu, modos, apelido, expulsão, moderação) chegam
 * como eventos estruturados e são montadas aqui no idioma do usuário.
 * Os mestres apagam linhas, limpam o chat e veem as apagadas com o /audit
 * (docs/spec/chat-delete.md).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { translate, useLanguage, useT } from '../../i18n/index.js';
import { createChatCommands } from '../chatCommands.js';
import { CHAT_RUN_TTL_MS, registerMainChat } from '../chatRun.js';
import { postWindows } from '../windows.js';
import { MacrosDialog, useRoomMacros } from '../macros/MacrosDialog.js';
import { findMacro, macroLines, useMyMacros } from '../macros/model.js';
import '../macros/macros.css';
import { TALEMARK_PALETTE, type ChatChannel, type ChatEvent, type ChatMessage, type ChatPersona, type DeletedChat, type DeletedChatLine, type DeskFile, type RollResult } from '../../shared.js';
import { sessionLineText } from '../clock/clockModel.js';
import { ROOM_TYPING_TTL_MS, describeError, useApp } from '../../store.js';
import { api } from '../../api.js';
import { canDeleteLine, deletePrompt, deletedHint, lineAuthor, lineText, withDeleted, type AuditedMessage } from '../chatDelete.js';
import { TalemarkView } from '../Talemark.js';
import { historyFor, remember, step } from '../inputHistory.js';
import { AttachmentView } from '../Attachment.js';
import { droppedImage, fetchDroppedImage, imageFileName, looksLikeImageDrag, type DroppedImage } from '../droppedImage.js';
import { hasFileDrag, readFileDrag } from '../../cloud/fileDrag.js';
import { registerPluginDrop } from '../../plugins/web/pluginDrag.js';
import { CloudFilePicker } from '../../cloud/CloudFilePicker.js';
import { serverUrl } from '../../serverUrl.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../../ui/ContextMenu.js';
import { askConfirm } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { useLongPress } from '../../ui/touch.js';
import { AVATAR_DRAG_NAME } from '../../social/avatarUrl.js';
import { DieIcon } from '../../ui/dice-icons.js';
import { Button } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import { useMobile } from '../../ui/mobile.js';

const QUICK_DICE = [4, 6, 8, 10, 12, 20, 100] as const;
/** linhas do mesmo autor dentro deste intervalo não repetem o horário */
const GROUP_MS = 3 * 60_000;
/** sem tecla por este tempo, o "digitando…" é desligado */
const TYPING_IDLE_MS = 5000;

type LocalLine = { id: string; createdAt: number; text: string };
/** Anexo preso acima da caixa do chat (BRAZ-26): arquivo do computador (`file`) ou do Desk Cloud (`storedId`). */
type Attached = { id: number; name: string; preview: string | null; sending: boolean; file?: File; storedId?: string };
/** `deleted`: a linha apagada que o /audit trouxe de volta */
type Line = { kind: 'msg'; m: ChatMessage; at: number; deleted?: DeletedChatLine | undefined } | { kind: 'local'; l: LocalLine; at: number } | { kind: 'welcome'; at: number };

/** A mensagem (ou rolagem) pertence a este canal, do ponto de vista de `me`? */
export function inChannel(item: { userId: string; channel?: ChatChannel | undefined }, channel: ChatChannel | undefined, me: string | undefined): boolean {
  const c = item.channel;
  if (!channel) return !c;
  if (!c || c.kind !== channel.kind) return false;
  if (c.kind === 'group') return channel.kind === 'group' && c.groupId === channel.groupId;
  if (channel.kind !== 'pvt') return false;
  return (item.userId === me && c.to === channel.to) || (item.userId === channel.to && c.to === me);
}

/** Linhas que uma macro, com as que ela chama, roda de uma vez. */
const MACRO_RUN_LINES = 100;

/**
 * `channel` ausente: chat principal da mesa. Com canal, é o módulo de conversa
 * privada (system/pvtChat) ou em grupo (system/groupPVTChat), com o mesmo log e
 * a mesma caixa de entrada.
 */
export function ChatModule({ channel }: { channel?: ChatChannel } = {}) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const me = useApp((s) => s.user?.id);
  const allMessages = useApp((s) => s.messages);
  const allRolls = useApp((s) => s.rolls);
  const channelKey = channel ? (channel.kind === 'pvt' ? `pvt:${channel.to}` : `group:${channel.groupId}`) : 'main';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages = useMemo(() => allMessages.filter((m) => inChannel(m, channel, me)), [allMessages, channelKey, me]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rolls = useMemo(() => allRolls.filter((r) => inChannel(r, channel, me)), [allRolls, channelKey, me]);
  const room = useApp((s) => s.room);
  // moderar o chat fica aqui, onde ele age (docs/spec/room-menus.md): só para quem administra a mesa
  const canModerate = useApp((s) => !!s.room && !!s.user && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm')));
  const setModerated = useApp((s) => s.setModerated);
  // apagar linhas, limpar o chat e o /audit (docs/spec/chat-delete.md): os mestres, no chat da mesa
  const canDelete = useApp((s) => s.serverFeatures.chatDelete) && canModerate && !channel;
  const deleteChatLine = useApp((s) => s.deleteChatLine);
  const clearChat = useApp((s) => s.clearChat);
  const removedVersion = useApp((s) => (s.room ? (s.chatRemovedVersion[s.room.id] ?? 0) : 0));
  /** o menu de uma linha (clique direito ou toque longo) */
  const [lineMenu, setLineMenu] = useState<{ anchor: MenuAnchor; m: ChatMessage } | null>(null);
  const welcome = useApp((s) => s.welcome);
  const sendChat = useApp((s) => s.sendChat);
  const rollDice = useApp((s) => s.rollDice);
  const leaveRoom = useApp((s) => s.leaveRoom);
  const setNick = useApp((s) => s.setNick);
  const sendTyping = useApp((s) => s.sendTyping);
  const attachFile = useApp((s) => s.attachFile);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** arquivo sendo arrastado por cima do chat: mostra a camada de "solte aqui" */
  const [dragging, setDragging] = useState(false);
  /**
   * Anexos presos acima da caixa (BRAZ-26): o clipe, o colar e o soltar só anexam, com a miniatura, o
   * nome e um × para tirar. Vão com a mensagem no Enter (ou no botão de enviar), um por vez, e cada um
   * mostra "enviando…" até chegar ao chat. `storedId`: arquivo que já está no Desk Cloud.
   */
  const [attached, setAttached] = useState<Attached[]>([]);
  const attachedSeq = useRef(0);
  const attachedRef = useRef(attached);
  attachedRef.current = attached;
  // fechou a conversa com anexos presos: as miniaturas deixam de existir
  useEffect(
    () => () => {
      for (const a of attachedRef.current) if (a.preview?.startsWith('blob:')) URL.revokeObjectURL(a.preview);
    },
    [],
  );
  const attachFiles = (files: Iterable<File>) => {
    const added: Attached[] = [...files].map((file) => ({
      id: ++attachedSeq.current,
      name: file.name,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      sending: false,
      file,
    }));
    if (added.length) setAttached((list) => [...list, ...added]);
  };
  /** Arquivos escolhidos no Desk Cloud: ficam presos como os do computador, com a miniatura nas imagens. */
  const attachStored = (files: DeskFile[]) => {
    const added: Attached[] = files.map((file) => ({
      id: ++attachedSeq.current,
      name: file.name,
      preview: file.mime.startsWith('image/') && file.mime !== 'image/svg+xml' ? serverUrl(file.thumbUrl ?? file.url) : null,
      sending: false,
      storedId: file.id,
    }));
    if (added.length) setAttached((list) => [...list, ...added]);
  };
  const detach = (item: Attached) => {
    if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
    setAttached((list) => list.filter((a) => a.id !== item.id));
  };
  /** Sobe e manda os anexos em ordem; `caption` vai com o primeiro. */
  const sendAttached = async (items: Attached[], caption: string) => {
    const ids = new Set(items.map((a) => a.id));
    setAttached((list) => list.map((a) => (ids.has(a.id) ? { ...a, sending: true } : a)));
    for (const [index, item] of items.entries()) {
      const text = index === 0 ? caption : '';
      if (item.file) await attachFile(item.file, channel, text);
      else if (item.storedId) useApp.getState().attachStoredFile(item.storedId, channel, text);
      detach(item);
    }
  };
  /**
   * Imagem arrastada sem arquivo (BRAZ-22): a foto de alguém, um token, uma imagem de outro site.
   * Baixa e anexa como arquivo, como o clipe; se o site não deixar baixar, vai como imagem no texto da
   * caixa, para seguir com a mensagem.
   */
  const attachDroppedImage = async (image: DroppedImage) => {
    const roomId = useApp.getState().activeRoomId;
    const blob = await fetchDroppedImage(image.url, useApp.getState().token).catch(() => null);
    // trocou de mesa enquanto baixava: a imagem não vai para a mesa nova
    if (useApp.getState().activeRoomId !== roomId) return;
    if (blob) attachFiles([new File([blob], imageFileName(image.name, blob.type, t('chat.drop.imageName')), { type: blob.type })]);
    else if (/^https?:/i.test(image.url)) setText((current) => `${current ? `${current} ` : ''}[§I ${image.url.replace(/\]/g, '%5D').replace(/\s/g, '%20')}]`);
  };
  // a imagem arrastada de um plugin web (o retrato de uma ficha, `data-drag`) não chega como arraste
  // do navegador: o anfitrião do plugin acende a camada e solta aqui, e ela fica presa como as do
  // painel Arquivos (pluginDrag.ts)
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    return registerPluginDrop(el, {
      hover: setDragging,
      drop: (file) =>
        setAttached((list) => [
          ...list,
          { id: ++attachedSeq.current, name: file.name, preview: file.mime.startsWith('image/') && file.mime !== 'image/svg+xml' ? file.url : null, sending: false, storedId: file.id },
        ]),
    });
  }, []);
  // o arraste pode acabar sem sair do chat pelo dragleave (soltou em outro lugar, cancelou): a
  // camada sai junto com ele
  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener('dragend', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragend', stop);
      window.removeEventListener('drop', stop);
    };
  }, [dragging]);

  const [text, setText] = useState('');
  const [persona, setPersona] = useState<'none' | 'narrator' | 'npc'>('none');
  const [npcName, setNpcName] = useState('');
  const [hiddenBefore, setHiddenBefore] = useState(0);
  const [hideSystem, setHideSystem] = useState(false);
  // o /audit: as apagadas voltam ao lugar delas, marcadas. Quem deixa de ser mestre sai dele
  const [auditOn, setAuditOn] = useState(false);
  const [audit, setAudit] = useState<DeletedChat | null>(null);
  const auditing = auditOn && canDelete;
  const stopAudit = () => {
    setAuditOn(false);
    setAudit(null);
  };
  // ligado, busca as apagadas, e relê a cada linha que sai do chat
  useEffect(() => {
    const token = useApp.getState().token;
    if (!auditing || !room || !token) return;
    let alive = true;
    api.deletedChat(token, room.id).then(
      (data) => {
        if (alive) setAudit(data);
      },
      (e: unknown) => {
        if (alive) toast(describeError(e), 'error');
      },
    );
    return () => {
      alive = false;
    };
  }, [auditing, room?.id, removedVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const commands = useMemo(() => createChatCommands(language, (key, params) => translate(language, key, params)), [language]);
  // macros: da mesa, minhas desta mesa e minhas globais (nessa ordem), chamadas por /nome
  const [macrosOpen, setMacrosOpen] = useState(false);
  /** o clipe: o menu (do computador ou do Desk Cloud) e a lista do Desk Cloud */
  const [attachMenu, setAttachMenu] = useState<MenuAnchor | null>(null);
  const [cloudPicking, setCloudPicking] = useState(false);
  const { macros: roomMacros } = useRoomMacros();
  const myMacros = useMyMacros(me);
  const [local, setLocal] = useState<LocalLine[]>([]);
  const [colorOpen, setColorOpen] = useState(false);
  const mobile = useMobile();
  // celular: a fileira de dados ou a de formatação, quando pedida
  const [extra, setExtra] = useState<'dice' | 'format' | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<{ on: boolean; timer: number | null }>({ on: false, timer: null });

  /** Avisa "digitando…" só quando o estado muda; desliga sozinho após um tempo parado. */
  function noteTyping(value: string) {
    const state = typingRef.current;
    // "digitando…" só no chat principal
    const on = !channel && value.trim().length > 0 && !value.startsWith('/');
    if (state.timer !== null) window.clearTimeout(state.timer);
    state.timer = on ? window.setTimeout(() => noteTyping(''), TYPING_IDLE_MS) : null;
    if (state.on !== on) {
      state.on = on;
      sendTyping(on);
    }
  }
  useEffect(() => () => {
    const state = typingRef.current;
    if (state.timer !== null) window.clearTimeout(state.timer);
    // o chat saiu da tela (outra aba, outra página) com o aviso ligado: desliga, senão ficava preso
    if (state.on) {
      state.on = false;
      useApp.getState().sendTyping(false);
    }
  }, []);

  const rollsByKey = useMemo(() => {
    const m = new Map<string, RollResult>();
    for (const r of [...rolls, ...(auditing && audit ? audit.rolls : [])]) m.set(`${r.userId}:${r.createdAt}`, r);
    return m;
  }, [rolls, auditing, audit]);

  const lines = useMemo<Line[]>(() => {
    const shown = auditing && audit ? withDeleted(messages, audit.lines) : messages.map((m): AuditedMessage => ({ m }));
    const all: Line[] = [
      ...shown
        // o /prink esconde as linhas de sistema, menos as do relógio da sessão (marcam o começo e o fim)
        .filter(({ m }) => m.createdAt >= hiddenBefore && !(hideSystem && m.kind === 'system' && m.event?.type !== 'gameSession'))
        .map(({ m, deleted }) => ({ kind: 'msg' as const, m, at: m.createdAt, deleted })),
      ...local.map((l) => ({ kind: 'local' as const, l, at: l.createdAt })),
    ];
    // boas-vindas da mesa: uma linha do chat principal, na hora em que entrei (sobe com as novas)
    if (!channel && room?.welcomeMessage && welcome?.roomId === room.id && welcome.at >= hiddenBefore) all.push({ kind: 'welcome', at: welcome.at });
    return all.sort((a, b) => a.at - b.at);
  }, [messages, local, hiddenBefore, hideSystem, channel, room?.id, room?.welcomeMessage, welcome, auditing, audit]);

  // o log segue o fim enquanto o usuário estiver nele; ao (re)montar — voltar de outra aba — abre no fim.
  // Pela última linha, não pelo tamanho: no teto de mensagens da store, cada nova empurra uma antiga
  // para fora e o tamanho não muda mais
  const stickRef = useRef(true);
  const lastLine = lines.at(-1);
  useLayoutEffect(() => {
    const el = logRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [lines.length, lastLine]);
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    const onScroll = () => {
      stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    };
    // imagens que terminam de carregar aumentam o log depois do primeiro render
    const onLoad = () => {
      if (stickRef.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('load', onLoad, true);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('load', onLoad, true);
    };
  }, []);

  const say = (line: string) => setLocal((ls) => [...ls, { id: `${Date.now()}-${ls.length}`, createdAt: Date.now(), text: line }]);

  const currentPersona = (): ChatPersona | undefined =>
    persona === 'narrator' ? { kind: 'narrator' } : persona === 'npc' && npcName.trim() ? { kind: 'npc', name: npcName.trim() } : undefined;

  // histórico do que foi digitado (seta para cima/baixo), por mesa e conversa
  const historyKey = `${room?.id ?? ''}:${channelKey}`;
  const historyIndex = useRef(-1);

  function submit() {
    const raw = text.trim();
    const files = attached.filter((a) => !a.sending);
    if (!raw && !files.length) return;
    if (raw) remember(historyKey, raw);
    historyIndex.current = -1;
    setText('');
    typingRef.current.on = false; // o servidor desliga o aviso ao receber a mensagem
    if (typingRef.current.timer !== null) window.clearTimeout(typingRef.current.timer);
    // com anexo, o texto comum vira a legenda do primeiro (BRAZ-26); comando, macro ou fala de narrador
    // e NPC vão antes, sozinhos, porque o anexo não leva nada disso
    const caption = files.length && raw && !commands.canHandle(raw) && !currentPersona() ? raw : '';
    if (raw && !caption) runLine(raw, 0);
    if (files.length) void sendAttached(files, caption);
  }

  /**
   * Uma linha digitada (ou de uma macro): texto, comando ou macro. `run` é o que resta da execução
   * inteira: uma macro (com as que ela chama) roda no máximo MACRO_RUN_LINES linhas, e uma chamada
   * funda demais para a execução toda, com um aviso só (uma macro que chama a si mesma geraria
   * milhares de avisos e travaria o chat).
   */
  function runLine(raw: string, depth: number, run = { left: MACRO_RUN_LINES, stopped: false }) {
    if (run.stopped) return;
    if (!commands.canHandle(raw)) {
      sendChat(raw, 'chat', currentPersona(), channel);
      return;
    }
    if (!commands.containsRegisteredCommand(raw)) {
      const request = commands.decode(raw);
      const macro = request ? findMacro(request.commandId, roomMacros, room ? (myMacros.rooms[room.id] ?? []) : [], myMacros.global) : null;
      if (macro) {
        // macro chamando macro: até 3 níveis (evita laço)
        if (depth >= 3) {
          say(t('macro.tooDeep', { name: macro.name }));
          run.stopped = true;
          return;
        }
        const lines = macroLines(macro);
        if (!lines.length) say(t('macro.emptyRun', { name: macro.name }));
        for (const line of lines) {
          if (run.stopped) return;
          if (run.left <= 0) {
            say(t('macro.tooLong', { name: macro.name, max: MACRO_RUN_LINES }));
            run.stopped = true;
            return;
          }
          run.left -= 1;
          runLine(line, depth + 1, run);
        }
        return;
      }
    }
    const response = commands.execute(raw, {
      t,
      sendChat: (body, kind, persona) => sendChat(body, kind, persona, channel),
      rollDice: (expression) => rollDice(expression, null, channel),
      leaveRoom,
      setNick,
      persona: currentPersona,
      hideBefore: (at) => {
        setHiddenBefore(at);
        if (at > 0) setLocal([]);
      },
      hideSystem: setHideSystem,
      toggleAudit: canDelete ? () => (auditOn ? stopAudit() : setAuditOn(true)) : undefined,
      say,
      openMacros: () => setMacrosOpen(true),
    });
    const feedback = commands.describe(response);
    if (feedback) say(feedback);
  }

  // o chat principal roda as linhas pedidas de fora (painel de macros), uma vez cada, mesmo com uma
  // cópia dele montada; a pedida com ele fora da tela roda quando ele aparece, se ainda for recente
  useEffect(() => (channel ? undefined : registerMainChat()), [channel]);
  const chatRun = useApp((s) => s.chatRun);
  useEffect(() => {
    if (channel || !chatRun || chatRun.done || chatRun.roomId !== room?.id || Date.now() - chatRun.at > CHAT_RUN_TTL_MS) return;
    if (!useApp.getState().takeChatRun(chatRun.nonce)) return;
    postWindows({ t: 'chat-ran', roomId: chatRun.roomId, runId: chatRun.id });
    runLine(chatRun.text, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRun?.nonce]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
      return;
    }
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey && !e.nativeEvent.isComposing) {
      const next = step(historyFor(historyKey), historyIndex.current, text, e.key === 'ArrowUp' ? 'older' : 'newer');
      if (!next) return; // texto editado: a seta anda com o cursor
      e.preventDefault();
      historyIndex.current = next.index;
      setText(next.text);
      // cursor no fim da linha trazida
      const el = e.currentTarget;
      requestAnimationFrame(() => el.setSelectionRange(next.text.length, next.text.length));
    }
  }

  function wrapSelection(open: string, close: string) {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    setText(text.slice(0, a) + open + text.slice(a, b) + close + text.slice(b));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + open.length, b + open.length);
    });
  }

  /** Apaga uma linha do chat da mesa, perguntando antes (Shift+clique na lixeira apaga sem perguntar). */
  async function askDelete(m: ChatMessage, skipAsk: boolean) {
    if (!skipAsk && !(await askConfirm({ ...deletePrompt(t, m), action: t('chat.delete.action'), danger: true }))) return;
    if (!deleteChatLine(m.id)) toast(t('chat.delete.offline'), 'error');
  }

  async function askClear() {
    if (!(await askConfirm({ title: t('chat.clear.title'), message: t('chat.clear.message'), action: t('chat.clear.action'), danger: true }))) return;
    if (!clearChat()) toast(t('chat.delete.offline'), 'error');
  }

  /** O menu de uma linha: copiar o texto (o clique direito e o toque longo não abrem o do navegador) e apagar. */
  const lineEntries = (m: ChatMessage): MenuEntry[] => [
    { kind: 'title', label: t(m.kind === 'roll' ? 'chat.line.roll' : 'chat.line.message', { name: lineAuthor(t, m) }) },
    {
      label: t('chat.line.copy'),
      onSelect: () =>
        void navigator.clipboard?.writeText(lineText(m)).then(
          () => toast(t('chat.line.copied'), 'success'),
          () => toast(t('chat.line.copyFailed'), 'error'),
        ),
    },
    { kind: 'separator' },
    { label: t(m.kind === 'roll' ? 'chat.delete.roll.menu' : 'chat.delete.menu'), danger: true, icon: <Icon name="trash" size={14} />, onSelect: () => void askDelete(m, false) },
  ];
  const deletedWhen = (ms: number) => new Date(ms).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  // monta o log com separadores de data e agrupamento por autor
  const rendered: ReactNode[] = [];
  let lastDay = '';
  let prev: Line | null = null;
  const dayFmt = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' });
  for (const line of lines) {
    const day = new Date(line.at).toDateString();
    if (day !== lastDay) {
      rendered.push(
        <div key={`d${line.at}`} className="rd-chat__date">
          <span>{dayFmt.format(line.at)}</span>
        </div>,
      );
      lastDay = day;
      prev = null;
    }
    if (line.kind === 'welcome') {
      rendered.push(
        <div key="welcome" className="rd-chat__welcome">
          <TalemarkView text={room?.welcomeMessage ?? ''} />
        </div>,
      );
      prev = null;
      continue;
    }
    if (line.kind === 'local') {
      rendered.push(
        <div key={line.l.id} className="rd-chat__line rd-chat__line--local">
          <TalemarkView text={line.l.text} />
        </div>,
      );
      prev = null;
      continue;
    }
    const m = line.m;
    const grouped =
      prev?.kind === 'msg' && prev.m.userId === m.userId && m.kind !== 'system' && prev.m.kind !== 'system' && m.createdAt - prev.m.createdAt < GROUP_MS;
    const props = { m, roll: rollsByKey.get(`${m.userId}:${m.createdAt}`), grouped };
    if (line.deleted) {
      rendered.push(
        <DeletedEntry key={m.id} hint={deletedHint(t, line.deleted, deletedWhen)}>
          <ChatLine {...props} />
        </DeletedEntry>,
      );
    } else if (canDelete && canDeleteLine(m)) {
      rendered.push(
        <DeletableEntry
          key={m.id}
          label={t(m.kind === 'roll' ? 'chat.delete.roll.label' : 'chat.delete.label', { name: lineAuthor(t, m) })}
          onDelete={(skipAsk) => void askDelete(m, skipAsk)}
          onMenu={(anchor) => setLineMenu({ anchor, m })}
        >
          <ChatLine {...props} />
        </DeletableEntry>,
      );
    } else rendered.push(<ChatLine key={m.id} {...props} />);
    prev = line;
  }

  // as peças da caixa de digitar: no computador, numa barra só; no celular, o enviar ao lado da caixa,
  // e os dados e a formatação abrem quando pedidos (docs/spec/mobile.md)
  const textBox = (
    <textarea
      ref={inputRef}
      className="rd-chat__textarea"
      rows={1}
      // o teclado do celular mostra "Enviar" na tecla
      enterKeyHint="send"
      value={text}
      placeholder={t(attached.some((a) => !a.sending) ? 'chat.input.withAttachment' : 'chat.input.prompt')}
      aria-label={t('chat.input.prompt')}
      onChange={(e) => {
        // editou: as setas voltam a ser do cursor até a caixa ficar vazia
        historyIndex.current = -1;
        setText(e.target.value);
        noteTyping(e.target.value);
      }}
      onKeyDown={onKeyDown}
      onPaste={(e) => {
        // colar imagem (print) anexa; vai com a mensagem (BRAZ-26)
        const images = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
        if (images.length) {
          e.preventDefault();
          attachFiles(images);
        }
      }}
    />
  );
  const attachTool = (
    <>
      <ToolButton
        hint={t('chat.tool.sendFile')}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setAttachMenu({ x: rect.left, y: rect.top - 4, up: true });
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="m20 11.5-8.2 8.2a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" strokeLinecap="round" />
        </svg>
      </ToolButton>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        aria-hidden="true"
        onChange={(e) => {
          if (e.target.files) attachFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </>
  );
  const macroTool = (
    <ToolButton hint={t('macro.manager')} onClick={() => setMacrosOpen(true)}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
        <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
      </svg>
    </ToolButton>
  );
  // limpar o chat da mesa inteiro, para todos (docs/spec/chat-delete.md)
  const clearTool = canDelete ? (
    <ToolButton hint={t('chat.clear')} onClick={() => void askClear()}>
      <Icon name="eraser" size={16} />
    </ToolButton>
  ) : null;
  // só no chat da mesa: as conversas privadas não são moderadas
  const moderateTool =
    !channel && canModerate ? (
      <ToolButton hint={t('room.moderate.on')} pressed={!!room?.moderated} onClick={() => setModerated(!room?.moderated)}>
        <Icon name="moderate" size={16} />
      </ToolButton>
    ) : null;
  const formatTools = (
    <>
      <ToolButton hint={t('chat.tool.bold')} onClick={() => wrapSelection('[§B]', '[§B]')}>
        <b>B</b>
      </ToolButton>
      <ToolButton hint={t('chat.tool.italic')} onClick={() => wrapSelection('[§T]', '[§T]')}>
        <i style={{ fontFamily: 'Georgia, serif' }}>I</i>
      </ToolButton>
      <ToolButton hint={t('chat.tool.underline')} onClick={() => wrapSelection('[§U]', '[§U]')}>
        <u>U</u>
      </ToolButton>
      <div style={{ position: 'relative' }}>
        <ToolButton hint={t('chat.tool.color')} onClick={() => setColorOpen((v) => !v)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="m15.5 3 5.5 5.5-9.6 9.6H5.9v-5.5zM3 20h18v2H3z" />
          </svg>
        </ToolButton>
        {colorOpen ? (
          <div className="rd-menu rd-chat__palette" role="menu" onMouseLeave={() => setColorOpen(false)}>
            {TALEMARK_PALETTE.slice(0, 16).map((c, i) => (
              <button
                key={c}
                type="button"
                role="menuitem"
                className="rd-chat__swatch"
                style={{ background: c }}
                aria-label={`${t('chat.tool.color')} ${i}`}
                onClick={() => {
                  wrapSelection(`[§K${i}]`, '[§O]');
                  setColorOpen(false);
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
      <span className="rd-chat__sep" />
      <select className="rd-chat__persona" value={persona} onChange={(e) => setPersona(e.target.value as typeof persona)} aria-label={t('chat.persona')}>
        <option value="none">{t('chat.persona.none')}</option>
        <option value="narrator">{t('chat.persona.narrator')}</option>
        <option value="npc">{t('chat.persona.npc')}</option>
      </select>
      {persona === 'npc' ? (
        <input
          className="rd-chat__npc"
          value={npcName}
          maxLength={40}
          onChange={(e) => setNpcName(e.target.value)}
          placeholder={t('chat.persona.npcName')}
          aria-label={t('chat.persona.npcName')}
        />
      ) : null}
    </>
  );
  const diceTools = QUICK_DICE.map((n) => (
    <ToolButton key={n} hint={t('chat.tool.dice', { n })} onClick={() => rollDice(`1d${n}`, null, channel)}>
      <DieIcon sides={n} />
    </ToolButton>
  ));

  return (
    <div
      ref={rootRef}
      className={`rd-chat${dragging ? ' rd-chat--drop' : ''}`}
      onDragOver={(e) => {
        // arquivos e imagens (inclusive a foto de alguém, que chega sem arquivo), e os do painel Arquivos
        if (!looksLikeImageDrag(e.dataTransfer.types, AVATAR_DRAG_NAME) && !hasFileDrag(e.dataTransfer)) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        // o dragleave vem de cada elemento de dentro que o ponteiro deixa: só sai de verdade quando
        // ele vai para fora do chat (ou da janela, com relatedTarget vazio)
        const next = e.relatedTarget as Node | null;
        if (!next || !e.currentTarget.contains(next)) setDragging(false);
      }}
      onDrop={(e) => {
        setDragging(false);
        // um arquivo do Desk Cloud (o painel Arquivos da mesa): fica anexado, sem subir de novo
        const cloudFile = readFileDrag(e.dataTransfer);
        if (cloudFile) {
          e.preventDefault();
          setAttached((list) => [...list, { id: ++attachedSeq.current, name: cloudFile.name, preview: null, sending: false, storedId: cloudFile.id }]);
          return;
        }
        if (e.dataTransfer.files.length) {
          e.preventDefault();
          attachFiles(e.dataTransfer.files);
          return;
        }
        // a imagem arrastada da própria página (a foto de alguém, um token) ou de outro site chega
        // sem arquivo, só com o endereço: sem isto, a caixa de texto colaria o endereço (BRAZ-22)
        const image = droppedImage(e.dataTransfer, AVATAR_DRAG_NAME);
        if (image) {
          e.preventDefault();
          void attachDroppedImage(image);
          return;
        }
        // "Files" sem arquivo nem imagem: a soltura fica aceita (o navegador não abre nada); um
        // link comum segue para a caixa de texto
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
    >
      <div className="rd-chat__log" ref={logRef} role="log" aria-live="polite">
        {rendered}
      </div>

      <div className="rd-chat__input">
        {channel ? <div className="rd-chat__typing" /> : <TypingLine />}
        {!channel && room?.moderated ? (
          <div className="rd-chat__moderated">
            <span>{t('chat.moderatedBanner')}</span>
            {canModerate ? (
              <Button variant="smallAction" title={t('room.moderate.off')} aria-label={t('room.moderate.off')} onClick={() => setModerated(false)}>
                {t('chat.moderated.end')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {auditing ? (
          <div className="rd-chat__moderated rd-chat__audit" role="status">
            <span>{t('chat.audit.banner')}</span>
            <Button variant="smallAction" aria-label={t('chat.audit.exit.label')} onClick={stopAudit}>
              {t('chat.audit.exit')}
            </Button>
          </div>
        ) : null}
        {attached.length ? (
          <ul className="rd-chat__attached" aria-label={t('chat.attached')}>
            {attached.map((a) => (
              <li key={a.id} className="rd-chat__attachedFile" title={a.name}>
                {a.preview ? <img className="rd-chat__attachedThumb" src={a.preview} alt="" /> : <Icon name="file" size={18} />}
                <span className="rd-chat__attachedName">{a.name}</span>
                {a.sending ? (
                  <>
                    <span className="rd-chat__attachedStatus">{t('chat.attached.sending')}</span>
                    <span className="rd-spinner" aria-hidden="true" />
                  </>
                ) : (
                  <button type="button" className="rd-chat__attachedRemove" aria-label={t('chat.attached.remove', { name: a.name })} title={t('chat.attached.remove', { name: a.name })} onClick={() => detach(a)}>
                    <Icon name="close" size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {mobile ? (
          <div className="rd-chat__box rd-chat__box--mobile">
            <div className="rd-chat__row">
              {textBox}
              {/* sem tirar o foco da caixa: o teclado continua aberto depois de enviar */}
              <button type="button" className="rd-chat__send" aria-label={t('chat.sendButton')} disabled={!text.trim() && !attached.some((a) => !a.sending)} onPointerDown={(e) => e.preventDefault()} onClick={submit}>
                <Icon name="send" size={20} />
              </button>
            </div>
            <div className="rd-chat__tools" role="toolbar" aria-label={t('chat.tools')}>
              {attachTool}
              {macroTool}
              {moderateTool}
              {clearTool}
              <ToolButton hint={t('chat.tool.diceBar')} pressed={extra === 'dice'} onClick={() => setExtra((x) => (x === 'dice' ? null : 'dice'))}>
                <DieIcon sides={20} />
              </ToolButton>
              <ToolButton hint={t('chat.tool.format')} pressed={extra === 'format'} onClick={() => setExtra((x) => (x === 'format' ? null : 'format'))}>
                <span className="rd-chat__aa">Aa</span>
              </ToolButton>
            </div>
            {extra === 'dice' ? (
              <div className="rd-chat__dice" role="toolbar" aria-label={t('chat.tool.diceBar')}>
                {diceTools}
              </div>
            ) : null}
            {extra === 'format' ? (
              <div className="rd-chat__tools" role="toolbar" aria-label={t('chat.tool.format')}>
                {formatTools}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="rd-chat__box">
            {textBox}
            <div className="rd-chat__tools" role="toolbar" aria-label={t('chat.tools')}>
              {attachTool}
              {macroTool}
              {moderateTool}
              {clearTool}
              {formatTools}
            </div>
            <div className="rd-chat__dice" role="toolbar" aria-label={t('chat.tool.diceBar')}>
              {diceTools}
            </div>
          </div>
        )}
      </div>
      {macrosOpen ? <MacrosDialog onClose={() => setMacrosOpen(false)} /> : null}
      {lineMenu ? <ContextMenu anchor={lineMenu.anchor} label={lineAuthor(t, lineMenu.m)} entries={lineEntries(lineMenu.m)} onClose={() => setLineMenu(null)} /> : null}
      {attachMenu ? (
        <ContextMenu
          anchor={attachMenu}
          label={t('chat.tool.sendFile')}
          entries={[
            { label: t('chat.attach.computer'), onSelect: () => fileInputRef.current?.click() },
            { label: t('chat.attach.cloud'), onSelect: () => setCloudPicking(true) },
          ]}
          onClose={() => setAttachMenu(null)}
        />
      ) : null}
      {cloudPicking ? (
        <CloudFilePicker
          title={t('chat.attach.cloudTitle')}
          action={t('chat.attach.cloudAction')}
          empty={t('chat.attach.cloudEmpty')}
          hint={t('chat.attach.cloudHint')}
          onPick={(files) => {
            attachStored(files);
            setCloudPicking(false);
          }}
          onClose={() => setCloudPicking(false)}
        />
      ) : null}
    </div>
  );
}

function ToolButton({ hint, onClick, pressed, children }: { hint: string; onClick: (e: MouseEvent<HTMLButtonElement>) => void; pressed?: boolean; children: ReactNode }) {
  return (
    <button type="button" className={`rd-chat__tool${pressed ? ' is-on' : ''}`} title={hint} aria-label={hint} aria-pressed={pressed} onClick={onClick}>
      {children}
    </button>
  );
}

/** [hh:mm] da linha (some se a pessoa desligou o horário em Configurações → Geral). */
function Time({ at, hidden }: { at: number; hidden: boolean }) {
  const show = useApp((st) => st.settings.showTimestamps);
  if (!show) return null;
  const s = new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return <span className={`rd-chat__time${hidden ? ' rd-chat__time--hidden' : ''}`}>[{s}]</span>;
}

function ChatLine({ m, roll, grouped }: { m: ChatMessage; roll?: RollResult | undefined; grouped: boolean }) {
  const t = useT();
  const author = m.persona?.kind === 'narrator' ? t('chat.persona.narrator') : m.persona?.kind === 'npc' ? m.persona.name : (m.nick ?? m.displayName);
  const name = (
    <span className="rd-chat__name">
      {author}
      {m.persona?.kind === 'npc' ? <span className="rd-chat__npcTag">NPC</span> : null}
    </span>
  );

  if (m.kind === 'system' && m.event?.type === 'gameSession') return <SessionLine m={m} event={m.event} />;

  if (m.kind === 'system') {
    const verb = m.event ? describeEvent(m.event, t) : null;
    return (
      <div className="rd-chat__line rd-chat__line--system">
        <Time at={m.createdAt} hidden={false} />{' '}
        {verb ? (
          <>
            <span className="rd-chat__avatar" style={{ background: m.color }} />
            {m.event?.type === 'nick' ? <span className="rd-chat__name">{m.event.from}</span> : name}
            {m.nick && m.event?.type !== 'nick' ? <span className="rd-chat__login"> ({m.displayName})</span> : null}{' '}
            <span className="rd-chat__verb">{verb}</span>
            {m.event?.type === 'leave' && m.event.message ? (
              <span className="rd-chat__farewell">
                {' '}
                “<TalemarkView text={m.event.message} inline />”
              </span>
            ) : null}
          </>
        ) : (
          <TalemarkView text={m.text} inline />
        )}
      </div>
    );
  }

  if (m.kind === 'roll') {
    return (
      <div className="rd-chat__line rd-chat__line--roll">
        <Time at={m.createdAt} hidden={grouped} /> <span className="rd-chat__rollIcon" aria-hidden="true">
          <DieIcon sides={20} size={14} />
        </span> {name}{' '}
        {roll?.detail ? <RollView roll={roll} /> : <span>{m.text}</span>}
      </div>
    );
  }

  if (m.kind === 'action' || m.kind === 'laugh') {
    return (
      <div className={`rd-chat__line rd-chat__line--${m.kind}`}>
        <Time at={m.createdAt} hidden={grouped} /> {name} <TalemarkView text={m.text} inline />
      </div>
    );
  }

  return (
    <div className={`rd-chat__line${m.persona?.kind === 'narrator' ? ' rd-chat__line--narrator' : ''}`}>
      <Time at={m.createdAt} hidden={grouped} /> {name}
      <span className="rd-chat__colon">:</span>{' '}
      <span className="rd-chat__text">
        {m.attachment && m.text === m.attachment.name ? null : <TalemarkView text={m.text} inline />}
        {m.attachment ? <AttachmentView attachment={m.attachment} /> : null}
      </span>
    </div>
  );
}

/**
 * A linha que o mestre pode apagar (docs/spec/chat-delete.md): a lixeira no fim, ao passar o mouse
 * ou com o foco (Shift+clique apaga sem perguntar), e o menu no clique direito ou no toque longo. Com
 * texto selecionado nela, o clique direito é o do navegador, para copiar.
 */
function DeletableEntry({ label, onDelete, onMenu, children }: { label: string; onDelete: (skipAsk: boolean) => void; onMenu: (anchor: MenuAnchor) => void; children: ReactNode }) {
  const t = useT();
  const press = useLongPress();
  const handlers = press.bind(onMenu);
  return (
    <div
      className="rd-chat__entry rd-chat__entry--gm"
      {...handlers}
      onContextMenu={(e) => {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed && selection.toString().trim() && e.currentTarget.contains(selection.anchorNode)) return;
        handlers.onContextMenu(e);
      }}
    >
      {children}
      <button type="button" className="rd-chat__lineDelete" aria-label={label} title={t('chat.delete.hint')} onClick={(e) => onDelete(e.shiftKey)}>
        <Icon name="trash" size={13} />
      </button>
    </div>
  );
}

/** A linha apagada no /audit: apagada na cor, com "(apagada)" e, na dica, quem apagou e quando. */
function DeletedEntry({ hint, children }: { hint: string; children: ReactNode }) {
  const t = useT();
  return (
    <div className="rd-chat__entry is-deleted">
      {children}
      <span className="rd-chat__deletedMark" title={hint}>
        {t('chat.deleted.mark')}
      </span>
    </div>
  );
}

/**
 * "rolou 1d8 + 1 = 6 {[5] + 1}". Com os dados 3D ainda rolando nesta tela, "rolou 1d8 + 1 = …": o
 * resultado aparece quando eles param (docs/spec/dice-3d.md, "Resultado depois da animação").
 */
function RollView({ roll }: { roll: RollResult }) {
  const t = useT();
  const rolling = useApp((s) => s.rollingDice[roll.id] === true);
  const d = roll.detail!;
  if (rolling) {
    return (
      <span className="rd-roll">
        <span className="rd-roll__verb">{t('chat.rolled')}</span> <span className="rd-roll__expr">{roll.expression}</span> ={' '}
        <span className="rd-roll__total is-rolling" title={t('chat.rolling')} aria-label={t('chat.rolling')}>
          …
        </span>
        {roll.label ? <span className="rd-roll__label"> {roll.label}</span> : null}
      </span>
    );
  }
  let result: string;
  if (d.outcome) {
    const n = d.successes ?? 0;
    result =
      d.outcome === 'criticalFailure' ? t('dice.criticalFailure') : d.outcome === 'failure' ? t('dice.failure') : n === 1 ? t('dice.oneSuccess') : t('dice.nSuccesses', { n });
  } else {
    result = String(roll.total);
  }
  return (
    <span className="rd-roll">
      <span className="rd-roll__verb">{t('chat.rolled')}</span> <span className="rd-roll__expr">{roll.expression}</span> = <span className="rd-roll__total">{result}</span>{' '}
      <span className="rd-roll__detail">
        {'{'}
        {d.items.map((it, i) => {
          const sign = it.sign < 0 ? ' − ' : i > 0 ? ' + ' : '';
          if (it.kind === 'value') return <span key={i}>{`${sign}${it.value}`}</span>;
          const values = it.kind === 'fudge' ? it.results.map((r) => (r > 0 ? '+' : r < 0 ? '−' : '0')) : it.results.map(String);
          return (
            <span key={i}>
              {sign}[<span className="rd-roll__dice">{values.join(', ')}</span>]
            </span>
          );
        })}
        {'}'}
      </span>
      {roll.label ? <span className="rd-roll__label"> {roll.label}</span> : null}
    </span>
  );
}

/**
 * O "R" ao lado da aba do chat: os dados da mesa usam números aleatórios reais do random.org (uma
 * regra de jogo; docs/spec/dice.md, "Fonte do sorteio"). Se a última rolagem da mesa não conseguiu
 * o random.org, o "R" fica riscado até uma rolagem voltar a usá-lo. A letra vem do CSS e a marca
 * fica fora da árvore de acessibilidade: a aba continua se chamando só "Chat".
 */
export function DiceSourceMark() {
  const t = useT();
  const active = useApp((s) => s.room?.gameRules.diceSource === 'random.org' && s.serverFeatures.randomOrg);
  const fallback = useApp((s) => s.rolls.at(-1)?.detail?.rng === 'fallback');
  if (!active) return null;
  return <span className={`rd-rngMark${fallback ? ' is-fallback' : ''}`} title={t(fallback ? 'dice.rng.mark.fallback' : 'dice.rng.mark')} aria-hidden="true" />;
}

/**
 * Um passo do relógio da sessão (docs/spec/session-clock.md): o começo e o fim dividem o chat, como a
 * data; a pausa e a volta ficam discretas. O texto já traz a hora, e quem agiu aparece na dica.
 */
function SessionLine({ m, event }: { m: ChatMessage; event: Extract<ChatEvent, { type: 'gameSession' }> }) {
  const t = useT();
  const { kind } = event.step;
  const automatic = event.step.kind === 'pause' && event.step.auto;
  return (
    <div className={`rd-chat__session rd-chat__session--${kind}`} title={automatic ? undefined : t('chat.event.session.by', { name: m.nick ?? m.displayName })}>
      <span className="rd-chat__sessionText">
        <Icon name={kind === 'pause' ? 'pause' : kind === 'end' ? 'stop' : 'play'} size={11} strokeWidth={0} filled />
        {sessionLineText(t, event.step, m.createdAt)}
      </span>
    </div>
  );
}

/** Texto da linha de sistema, no idioma de quem lê (o servidor só manda o evento). */
function describeEvent(event: ChatEvent, t: (key: string, params?: Record<string, string | number>) => string): string {
  switch (event.type) {
    case 'join':
      return t('chat.joined');
    case 'leave':
      return t('chat.left');
    case 'nick':
      return t('chat.event.nick', { to: event.to });
    case 'kick':
      return t(event.banned ? 'chat.event.ban' : 'chat.event.kick', { target: event.target.displayName });
    case 'moderated':
      return t(event.on ? 'chat.event.moderated.on' : 'chat.event.moderated.off');
    case 'groupCreated':
      return t('chat.event.groupCreated');
    case 'groupInvited':
      return t('chat.event.groupInvited', { target: event.target.displayName });
    case 'groupParted':
      return t('chat.event.groupParted');
    case 'chatCleared':
      return t('chat.event.chatCleared');
    case 'gameSession':
      return sessionLineText(t, event.step, event.step.at);
    case 'roomChanged':
      return event.value === undefined && event.field !== 'name'
        ? t(`chat.event.room.${event.field}.removed`)
        : t(`chat.event.room.${event.field}`, {
            value:
              event.field === 'tags'
                ? (event.value ?? '')
                    .split(', ')
                    .map((tag) => (t(`roomTag.${tag}`) === `roomTag.${tag}` ? tag : t(`roomTag.${tag}`)))
                    .join(', ')
                : (event.value ?? ''),
          });
    case 'modes': {
      const list = (modes: string[]) => modes.map((mode) => t(`mode.${mode}`)).join(', ');
      const parts: string[] = [];
      if (event.added.length) parts.push(t('chat.event.modes.added', { modes: list(event.added), target: event.target.displayName }));
      if (event.removed.length) parts.push(t('chat.event.modes.removed', { modes: list(event.removed), target: event.target.displayName }));
      return parts.join('; ');
    }
  }
}

/** "Fulano está digitando…" acima da caixa de entrada. */
function TypingLine() {
  const t = useT();
  const typing = useApp((s) => s.typing);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const [, tick] = useState(0);
  // avisos velhos (alguém fechou a aba no meio) somem sozinhos
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 2000);
    return () => window.clearInterval(timer);
  }, []);
  const names = Object.entries(typing)
    .filter(([, at]) => Date.now() - at < ROOM_TYPING_TTL_MS)
    .map(([id]) => members[id]?.nick ?? users.find((u) => u.id === id)?.displayName)
    .filter((n): n is string => !!n);
  const text =
    names.length === 0 ? '' : names.length === 1 ? t('chat.typing.one', { a: names[0]! }) : names.length === 2 ? t('chat.typing.two', { a: names[0]!, b: names[1]! }) : t('chat.typing.many');
  return (
    <div className="rd-chat__typing" aria-live="polite">
      {text}
    </div>
  );
}
